import styled, { css, keyframes } from 'styled-components';
import {
  BUTTON_SIZE_TOKENS,
  BUTTON_VARIANT_DANGER,
  BUTTON_VARIANT_FILLED,
  BUTTON_VARIANT_OUTLINED,
  BUTTON_VARIANT_SUCCESS,
  BUTTON_VARIANT_TEXT,
  BUTTON_VARIANT_TONAL,
  type ButtonSize,
  type ButtonVariant,
} from './Button.constants';

interface RootProps {
  $variant: ButtonVariant;
  $size: ButtonSize;
  $stretch?: boolean;
  $iconOnly?: boolean;
}

const variantStyles = (variant: ButtonVariant) => css`
  ${({ theme: { colors = {} } }) => {
    switch (variant) {
      case BUTTON_VARIANT_FILLED:
        return css`
          background: ${colors.primary ?? '#C1C1FF'};
          color: ${colors.onPrimary ?? '#1600A8'};
          &:hover:not(:disabled) {
            background: color-mix(
              in oklab,
              ${colors.primary ?? '#C1C1FF'} 92%,
              white
            );
          }
        `;
      case BUTTON_VARIANT_TONAL:
        return css`
          background: ${colors.secondaryContainer ?? '#363770'};
          color: ${colors.onSecondaryContainer ?? '#CDCCFF'};
          &:hover:not(:disabled) {
            background: color-mix(
              in oklab,
              ${colors.secondaryContainer ?? '#363770'} 92%,
              white
            );
          }
        `;
      case BUTTON_VARIANT_OUTLINED:
        return css`
          background: transparent;
          color: ${colors.onSurface ?? '#E4E1ED'};
          box-shadow: inset 0 0 0 1px ${colors.outlineVariant ?? '#464555'};
          &:hover:not(:disabled) {
            background: ${colors.surfaceContainerHigh ?? '#292932'};
          }
        `;
      case BUTTON_VARIANT_TEXT:
        return css`
          background: transparent;
          color: ${colors.onSurfaceVariant ?? '#C7C4D7'};
          &:hover:not(:disabled) {
            background: ${colors.surfaceContainerHigh ?? '#292932'};
            color: ${colors.onSurface ?? '#E4E1ED'};
          }
        `;
      case BUTTON_VARIANT_DANGER:
        return css`
          background: transparent;
          color: ${colors.error ?? '#FFB4AB'};
          box-shadow: inset 0 0 0 1px ${colors.error ?? '#FFB4AB'};
          &:hover:not(:disabled) {
            background: rgb(255 180 171 / 0.08);
          }
        `;
      case BUTTON_VARIANT_SUCCESS:
        return css`
          background: ${colors.successContainer ?? '#005318'};
          color: ${colors.onSuccessContainer ?? '#A7F5A6'};
          &:hover:not(:disabled) {
            background: color-mix(
              in oklab,
              ${colors.successContainer ?? '#005318'} 90%,
              white
            );
          }
        `;
      default:
        return null;
    }
  }}
`;

export const Root = styled.button<RootProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme: { spacing = {} } }) => spacing.level3 ?? '8px'};
  border: 0;
  border-radius: ${({ theme: { borderRadius = {} } }) =>
    borderRadius.full ?? '100px'};
  font-weight: 600;
  letter-spacing: 0.1px;
  white-space: nowrap;
  cursor: pointer;
  transition: background ${({ theme: { motion } }) => motion?.duration?.fast ?? '120ms'}
    ${({ theme: { motion } }) => motion?.easing?.standard ?? 'cubic-bezier(0.2,0,0,1)'};

  ${({ $size, $iconOnly }) => {
    const tokens = BUTTON_SIZE_TOKENS[$size];
    return css`
      height: ${tokens.height};
      padding: 0 ${$iconOnly ? '0' : tokens.padX};
      width: ${$iconOnly ? tokens.height : 'auto'};
      font-size: ${tokens.fontSize};
      & > svg,
      & > .material-symbols-rounded {
        font-size: ${tokens.iconSize};
        width: ${tokens.iconSize};
        height: ${tokens.iconSize};
      }
    `;
  }}

  ${({ $stretch }) =>
    $stretch &&
    css`
      width: 100%;
    `}

  ${({ $variant }) => variantStyles($variant)}

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme: { colors = {} } }) => colors.primary ?? '#C1C1FF'};
    outline-offset: 2px;
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

export const Spinner = styled.span`
  display: inline-block;
  width: 1em;
  height: 1em;
  border: 1.5px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: ${spin} 700ms linear infinite;
`;
